import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * BONQUERÉ — Mobile PDP Sticky Primary CTA
 *
 * ── THE ONE RULE THIS FILE EXISTS TO ENFORCE ─────────────────────────────────────────────────
 * The sticky bar shows an action ONLY when it currently holds a real, visible, enabled control to
 * forward that action to. Everything else here follows from that.
 *
 * Two of the three controls it proxies are injected by third-party apps AFTER the page renders —
 * STOQ (`.restock-rocket-button`) and Swym (`#swym-atw-pdp-button`). Neither is in the server HTML.
 * So during the first second of a page's life the buy box legitimately shows a different control
 * from the one it will settle on. Resolving early and caching the answer would produce a sticky bar
 * offering "Add to cart" for a product whose real CTA is "Notify When Available" — the exact
 * wrong-CTA flash this design is built to make impossible.
 *
 * Hence: NOTHING IS EVER CACHED. Every activation, every mutation and every click re-queries the
 * DOM and re-decides. Node identity is treated as disposable, which also makes the component
 * immune to variant morphs, section re-renders, Theme Editor reloads and app re-injection without
 * needing to know when any of those happen.
 *
 * ── WHAT IT DOES NOT DO ───────────────────────────────────────────────────────────────────────
 * No form is submitted here, no cart request is made, no restock subscription is sent, no wishlist
 * is written, no availability is computed. It does not read inventory, `release_status`, product
 * metafields, Liquid availability, STOQ's config objects, or `bnq-notify`. It reads the DOM the
 * shopper is actually looking at, and clicks the button they would have clicked.
 *
 * `bnq-notify` is deliberately absent from the resolver. STOQ is the authoritative Notify system
 * on this store; the native component remains in the DOM only because the buy-box architecture
 * still contains it, and it is never a proxy target.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bar
 * @property {HTMLButtonElement} primary
 * @property {HTMLElement} primaryLabel
 * @property {HTMLButtonElement} wishlist
 *
 * @extends Component<Refs>
 */
class BnqStickyCta extends Component {
  requiredRefs = ['bar', 'primary', 'primaryLabel', 'wishlist'];

  /**
   * Mobile only, expressed as the COMPLEMENT of the desktop query rather than as
   * `(max-width: 749px)`.
   *
   * Measured: at a viewport of exactly 749px neither `(max-width: 749px)` nor `(min-width: 750px)`
   * matched — the viewport width carries a fraction, so it sits in the gap between the two. The
   * stylesheet hides the bar on `(min-width: 750px)`, so a JS gate written as `max-width: 749px`
   * disagreed with it at that width: CSS showed the bar, JS refused to populate it, and the feature
   * was silently dead on that one viewport. Deriving both from the SAME query makes them unable to
   * disagree at any width, fractional or not.
   */
  #mqDesktop = window.matchMedia('(min-width: 750px)');

  /** @type {AbortController} */
  #controller = new AbortController();

  /** True once the real CTA row has been scrolled ABOVE the viewport. */
  #passed = false;

  /** True while the footer is close enough that the bar should release. */
  #nearFooter = false;

  /** True while STOQ's overlay is open. */
  #overlayOpen = false;

  /** @type {IntersectionObserver | null} */
  #ctaObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** Watches the buy-box row for app injection/removal. Scoped to that row, never the document. */
  /** @type {MutationObserver | null} */
  #boxObserver = null;

  /** Watches STOQ's overlay root for the overlay being opened or closed. */
  /** @type {MutationObserver | null} */
  #overlayObserver = null;

  /** Mirrors Swym's state class. Re-pointed if Swym ever replaces its node. */
  /** @type {MutationObserver | null} */
  #swymObserver = null;

  /** @type {Element | null} */
  #swymObserved = null;

  /** Coalesces bursts of mutations into one update per frame. */
  /** @type {number | null} */
  #frame = null;

  /** Coalesces continuous scroll geometry reads without re-resolving app controls every frame. */
  /** @type {number | null} */
  #scrollFrame = null;

  /** Whether the mobile observer set is currently active. */
  #active = false;

  /** @type {Element | null} */
  #ctaObserved = null;

  /** @type {Element | null} */
  #boxObserved = null;

  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    super.connectedCallback();

    const { signal } = this.#controller;

    this.refs.primary.addEventListener('click', this.#onPrimaryClick, { signal });
    this.refs.wishlist.addEventListener('click', this.#onWishlistClick, { signal });

    // Variant changes are announced on the section, exactly as product-form.js and Horizon's own
    // sticky bar listen for them.
    const section = this.#section;
    section?.addEventListener(ThemeEvents.variantUpdate, this.#schedule, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#schedule, { signal });

    // Clearing the puppet flag on the same events Horizon's sticky bar clears it on.
    document.addEventListener(ThemeEvents.cartUpdate, this.#clearPuppet, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#clearPuppet, { signal });

    // IntersectionObserver remains the primary boundary signal. The rAF-coalesced scroll listener
    // covers instant jumps and redesigned/morphed CTA geometry without resolving STOQ or Swym on
    // every frame; `scrollend` supplies one final exact read where the browser supports it.
    window.addEventListener('scrollend', this.#reevaluate, { signal });
    window.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    window.addEventListener('pageshow', this.#reevaluate, { signal });
    document.addEventListener('visibilitychange', this.#reevaluate, { signal });
    window.addEventListener('hashchange', this.#reevaluate, { signal });

    this.#mqDesktop.addEventListener('change', this.#onBreakpointChange, { signal });

    if (this.#isMobile) this.#activate();
  }

  /** The single source of truth for "is this the mobile presentation", shared with the stylesheet. */
  get #isMobile() {
    return !this.#mqDesktop.matches;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#deactivate();
    this.#controller.abort();
    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    if (this.#scrollFrame != null) cancelAnimationFrame(this.#scrollFrame);
    this.#frame = null;
    this.#scrollFrame = null;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────────────────────

  #onBreakpointChange = () => {
    if (this.#isMobile) this.#activate();
    else {
      this.#deactivate();
      this.#passed = false;
      this.#render(null);
    }
  };

  #activate() {
    if (this.#active) return;
    this.#active = true;
    this.#bindLiveTargets();

    // Releases the bar before the page bottom so the last of the PDP can always be read. Mirrors
    // the rootMargin Horizon's own sticky bar uses, so the two behave alike where they overlap.
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      this.#footerObserver = new IntersectionObserver(this.#onFooterIntersect, {
        rootMargin: '200px 0px 0px 0px',
      });
      this.#footerObserver.observe(footer);
    }

    this.#watchOverlay();
    this.#reevaluate();
  }

  #deactivate() {
    this.#active = false;
    this.#ctaObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#boxObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    this.#swymObserver?.disconnect();
    this.#ctaObserver = null;
    this.#footerObserver = null;
    this.#boxObserver = null;
    this.#overlayObserver = null;
    this.#swymObserver = null;
    this.#swymObserved = null;
    this.#ctaObserved = null;
    this.#boxObserved = null;
  }

  /** Rebinds observers after variant morphs or Theme Editor replacements without caching owners. */
  #bindLiveTargets() {
    const cta = this.#ctaRow;
    if (cta !== this.#ctaObserved) {
      this.#ctaObserver?.disconnect();
      this.#ctaObserver = null;
      this.#ctaObserved = cta;
      if (cta) {
        this.#ctaObserver = new IntersectionObserver(this.#onCtaIntersect);
        this.#ctaObserver.observe(cta);
      }
    }

    const box = this.#buyBox;
    if (box !== this.#boxObserved) {
      this.#boxObserver?.disconnect();
      this.#boxObserver = null;
      this.#boxObserved = box;
      if (box) {
        this.#boxObserver = new MutationObserver(this.#schedule);
        this.#boxObserver.observe(box, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'disabled', 'hidden'],
        });
      }
    }
  }

  // ── Scroll state ────────────────────────────────────────────────────────────────────────────

  /** @param {IntersectionObserverEntry[]} entries */
  #onCtaIntersect = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;

    // `boundingClientRect` comes with the entry, so distinguishing "not reached yet" (below the
    // viewport, top >= 0) from "scrolled past" (above it, top < 0) costs no layout read.
    this.#passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    this.#schedule();
  };

  /** @param {IntersectionObserverEntry[]} entries */
  #onFooterIntersect = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;
    this.#nearFooter = entry.isIntersecting;
    this.#schedule();
  };

  #reevaluate = () => {
    if (!this.#isMobile || document.visibilityState === 'hidden') return;
    this.#bindLiveTargets();
    const cta = this.#ctaRow;
    if (!cta) return;
    this.#passed = cta.getBoundingClientRect().bottom <= 0;
    this.#schedule();
  };

  #onScroll = () => {
    if (this.#scrollFrame != null || !this.#active || !this.#isMobile) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      const cta = this.#ctaRow;
      const passed = Boolean(cta && cta.getBoundingClientRect().bottom <= 0);
      if (passed === this.#passed) return;
      this.#passed = passed;
      this.#update();
    });
  };

  // ── Resolution ──────────────────────────────────────────────────────────────────────────────

  /** @returns {HTMLElement | null} */
  get #section() {
    const id = this.dataset.sectionId;
    return id ? document.getElementById(`shopify-section-${id}`) : null;
  }

  /** The buy-box action row. Re-queried on every use — never held. */
  get #buyBox() {
    return this.#section?.querySelector('product-form-component .bnq-atc-wrap') ?? null;
  }

  get #ctaRow() {
    return this.#buyBox?.querySelector('add-to-cart-component') ?? null;
  }

  /**
   * Whether an element is genuinely presented to the shopper.
   *
   * The `visibility` test is the load-bearing one. STOQ hides the native Add to Cart with
   * `visibility: hidden; opacity: 0`, not `display: none`, so a display-only check would resolve to
   * a button the shopper cannot see and hand them a purchase action on a product that is not for
   * sale. `getClientRects()` catches the remaining cases — a `display: none` ancestor, a detached
   * node — without a second pass over the ancestor chain.
   *
   * @param {Element | null | undefined} el
   */
  #isVisible(el) {
    if (!(el instanceof HTMLElement)) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    if (Number(style.opacity) === 0) return false;
    return el.getClientRects().length > 0;
  }

  /**
   * Decides which control, if any, the sticky bar currently stands in for.
   *
   * Order matters: STOQ first. When STOQ is present it has already hidden the native button, so
   * testing the native button first would be testing something the shopper cannot reach.
   *
   * @returns {{ mode: 'stoq' | 'add' | 'none', target: HTMLElement | null, label: string }}
   */
  #resolve() {
    const box = this.#buyBox;
    if (!box) return { mode: 'none', target: null, label: '' };

    const stoq = box.querySelector('.restock-rocket-button');
    if (this.#isVisible(stoq)) {
      return { mode: 'stoq', target: /** @type {HTMLElement} */ (stoq), label: this.#labelOf(stoq) };
    }

    const add = box.querySelector('add-to-cart-component [ref="addToCartButton"]');
    if (add instanceof HTMLButtonElement && this.#isVisible(add) && !add.disabled) {
      return { mode: 'add', target: add, label: this.#labelOf(add) };
    }

    // No authoritative control. Covers the STOQ pre-injection window, a disabled or unselected
    // variant, and any product where the buy box is showing something this component does not
    // proxy. The bar stays hidden rather than guessing.
    return { mode: 'none', target: null, label: '' };
  }

  /** @param {Element} el */
  #labelOf(el) {
    const inner = el.querySelector('.add-to-cart-text__content');
    return ((inner ?? el).textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  /** Swym's control. Scoped to the buy box so a wishlist button elsewhere can never be picked up. */
  get #swymButton() {
    const el = this.#buyBox?.querySelector('#swym-atw-pdp-button');
    return this.#isVisible(el) ? /** @type {HTMLButtonElement} */ (el) : null;
  }

  // ── Rendering ───────────────────────────────────────────────────────────────────────────────

  #schedule = () => {
    if (this.#frame != null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      if (this.#active && this.#isMobile && document.visibilityState !== 'hidden') {
        this.#bindLiveTargets();
        const cta = this.#ctaRow;
        this.#passed = Boolean(cta && cta.getBoundingClientRect().bottom <= 0);
      }
      this.#update();
    });
  };

  #update() {
    const eligible = this.#isMobile && document.visibilityState !== 'hidden' && this.#passed && !this.#nearFooter && !this.#overlayOpen;
    this.#render(eligible ? this.#resolve() : null);
  }

  /** @param {{ mode: string, target: HTMLElement | null, label: string } | null} resolved */
  #render(resolved) {
    const { bar, primary, primaryLabel, wishlist } = this.refs;
    const show = !!resolved && resolved.mode !== 'none';

    if (show && resolved) {
      if (primaryLabel.textContent !== resolved.label) primaryLabel.textContent = resolved.label;
      primary.hidden = false;
      bar.dataset.mode = resolved.mode;

      const swym = this.#swymButton;
      wishlist.hidden = !swym;
      if (swym) {
        this.#watchSwym(swym);
        wishlist.setAttribute('aria-pressed', String(swym.classList.contains('atw-button-added')));
      }
    } else {
      primary.hidden = true;
      wishlist.hidden = true;
      // Cleared rather than left behind, so the element can never hold a label describing a
      // control that is no longer the one the buy box is showing.
      primaryLabel.textContent = '';
      bar.dataset.mode = 'none';
    }

    bar.dataset.stuck = show ? 'true' : 'false';
    // Focus is never moved here. `inert` only stops the bar from being reached while it is
    // off-screen; the native, STOQ and Swym controls keep their own accessibility untouched.
    if (show) bar.removeAttribute('inert');
    else bar.setAttribute('inert', '');
  }

  // ── Proxies ─────────────────────────────────────────────────────────────────────────────────

  /** `inert` stops a person reaching a hidden control, but it does not stop a programmatic click.
   *  This makes the proxies genuinely inoperable whenever the bar is not presented. */
  get #presented() {
    return this.refs.bar.dataset.stuck === 'true';
  }

  #onPrimaryClick = () => {
    if (!this.#presented) return;

    // Re-resolved at click time, not read from render state: the buy box may have changed since
    // the bar was drawn, and forwarding to a stale node is the one failure that would reach the
    // cart.
    const { mode, target } = this.#resolve();
    if (!target) return;

    if (mode === 'add') {
      // The theme's own convention, honoured by product-form.js: `puppet` suppresses the native
      // button's fly-to-cart and burst, which would otherwise animate from an off-screen element.
      // Everything else — checkValidity, the quantity-max guard, the form submit, the cart request,
      // the drawer, the error path — runs exactly as a direct tap on that button.
      target.dataset.puppet = 'true';
      target.click();
      return;
    }

    target.click();

    // STOQ's overlay is a plain fixed div rather than a <dialog>, and it does not lock the page,
    // so the bar has to be told to stand down. The observer on the overlay root catches this on its
    // own; this is the immediate path so the bar does not linger for a frame.
    this.#watchOverlay();
    this.#syncOverlay();
    this.#schedule();
  };

  #onWishlistClick = () => {
    if (!this.#presented) return;
    // Swym owns the wishlist entirely. This forwards and then waits to be told the new state by the
    // class observer — it never assumes the toggle succeeded.
    this.#swymButton?.click();
  };

  #clearPuppet = () => {
    const add = this.#buyBox?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    if (add instanceof HTMLElement && add.dataset.puppet === 'true') add.dataset.puppet = 'false';
  };

  // ── App state mirrors ───────────────────────────────────────────────────────────────────────

  /**
   * STOQ mounts its overlay into a body-level `#restock-rocket`, which the app creates itself and
   * which may not exist yet. Attaching is idempotent and retried on each update until it lands.
   */
  #watchOverlay() {
    if (this.#overlayObserver) return;
    const root = document.getElementById('restock-rocket');
    if (!root) return;
    this.#overlayObserver = new MutationObserver(() => {
      this.#syncOverlay();
      this.#schedule();
    });
    this.#overlayObserver.observe(root, { childList: true });
    this.#syncOverlay();
  }

  #syncOverlay() {
    this.#overlayOpen = !!document.querySelector('.restock-rocket-wrapper');
  }

  /**
   * Mirrors Swym's state, which it publishes only as a class: `atw-button-added` when the product
   * is saved, `atw-button-add` when it is not. Its own `aria-label` is the static string
   * "Add to wishlist" in BOTH states, so the class is the only signal available.
   *
   * If Swym ever renames those classes the mirror goes stale, and that is the whole cost — the
   * forwarding click keeps working, and the primary CTA is untouched.
   *
   * @param {HTMLElement} button
   */
  #watchSwym(button) {
    if (this.#swymObserved === button) return;
    this.#swymObserver?.disconnect();
    this.#swymObserved = button;
    this.#swymObserver = new MutationObserver(() => {
      this.refs.wishlist.setAttribute(
        'aria-pressed',
        String(button.classList.contains('atw-button-added'))
      );
    });
    this.#swymObserver.observe(button, { attributes: true, attributeFilter: ['class'] });
  }
}

if (!customElements.get('bnq-sticky-cta')) {
  customElements.define('bnq-sticky-cta', BnqStickyCta);
}
